import { Box, MenuItem, Select, Typography, useTheme } from '@mui/material';
import {
  addDays,
  endOfWeek,
  format,
  isToday,
  isWithinInterval,
  startOfDay,
  startOfHour,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { ArchivedOrder } from '../../apiTypes';
import Loader from '../../components/Loader';
import PrimaryButton from '../../components/PrimaryButton';
import { StyledPaper } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { Lang, useLocale } from '../../contexts/locale.context';
import { dateLocaleMap, get2WeeksTimeRange } from '../../utils/dates';
import { useGetArchivedOrders } from '../Reports/useGetArchivedOrders';

function groupOrdersByHour(orders: ArchivedOrder[], isPrev?: boolean): Record<string, number> {
  const grouped: Record<string, number> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const order of orders) {
    const hour = startOfHour(isPrev ? addDays(order.created, 7) : order.created);
    const hourKey = hour.getTime();

    if (grouped[hourKey]) {
      grouped[hourKey] += order.paid?.total || 0;
    } else {
      grouped[hourKey] = order.paid?.total || 0;
    }
  }

  return grouped;
}

function groupOrdersByDay(orders: ArchivedOrder[], isPrev?: boolean): Record<string, number> {
  const grouped: Record<string, number> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const order of orders) {
    const day = startOfDay(isPrev ? addDays(order.created, 7) : order.created);
    const dayKey = day.getTime();

    if (grouped[dayKey]) {
      grouped[dayKey] += order.paid?.total || 0;
    } else {
      grouped[dayKey] = order.paid?.total || 0;
    }
  }

  return grouped;
}

function prepareWeeklyChartData(orders: ArchivedOrder[], lang: Lang) {
  const now = new Date();
  const lastWeek = {
    start: startOfWeek(now, { locale: dateLocaleMap[lang] }),
    end: endOfWeek(now, { locale: dateLocaleMap[lang] }),
  };
  const previousWeek = {
    start: startOfWeek(subWeeks(now, 1), { locale: dateLocaleMap[lang] }),
    end: endOfWeek(subWeeks(now, 1), { locale: dateLocaleMap[lang] }),
  };

  const lastWeekOrders = orders.filter((order) => isWithinInterval(order.created, lastWeek));
  const previousWeekOrders = orders.filter((order) => isWithinInterval(order.created, previousWeek));

  const groupedLastWeek = groupOrdersByDay(lastWeekOrders);
  const groupedPreviousWeek = groupOrdersByDay(previousWeekOrders, true);

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const dayKey = startOfDay(addDays(lastWeek.start, i)).getTime();
    return {
      title: format(dayKey, 'EEE', { locale: dateLocaleMap[lang] }),
      [t.current_period[lang]]: groupedLastWeek[dayKey] ?? 0,
      [t.last_week[lang]]: groupedPreviousWeek[dayKey] ?? 0,
    };
  });

  return chartData;
}

function prepareChartData(orders: ArchivedOrder[], lang: Lang) {
  const todayOrders = orders.filter((order) => isToday(order.created));
  const yesterdayOrders = orders.filter((order) => isToday(addDays(order.created, 7)));

  const groupedToday = groupOrdersByHour(todayOrders);
  const groupedYesterday = groupOrdersByHour(yesterdayOrders, true);

  const chartData = Array.from({ length: 24 }, (_, i) => {
    const hourKey = startOfHour(new Date().setHours(i)).getTime();
    return {
      title: format(hourKey, 'HH:mm'),
      [t.current_period[lang]]: groupedToday[hourKey] ?? 0,
      [t.last_week[lang]]: groupedYesterday[hourKey] ?? 0,
    };
  });

  return chartData;
}

const IncomeChart = () => {
  const { lang } = useLocale();
  const { cafeSlug, getCurrency } = useAdmin();
  const { palette } = useTheme();
  const [scale, setScale] = useState<'day' | 'week'>('week');
  const navitate = useNavigate();

  const navigateToArchive = useCallback(() => {
    navitate('./orderArchive');
  }, [navitate]);

  const period = useMemo(() => get2WeeksTimeRange(lang), [lang]);

  const { archivedOrders: orders, isLoading: isOrdersLoading } = useGetArchivedOrders(period);

  const hasOrders = orders?.length !== 0;

  const data = useMemo(
    () => (scale === 'day' ? prepareChartData(orders, lang) : prepareWeeklyChartData(orders, lang)),
    [lang, orders, scale]
  );

  return (
    <StyledPaper isCard sx={{ p: 2, width: 'auto' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">{t.sales[lang]}</Typography>
        <Select size="small" value={scale} onChange={(e) => setScale(e.target.value as 'day')}>
          <MenuItem value="day">{t.day[lang]}</MenuItem>
          <MenuItem value="week">{t.week[lang]}</MenuItem>
        </Select>
      </Box>

      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isOrdersLoading && <Loader open />}
        {!isOrdersLoading && !hasOrders && (
          <Typography color={(t) => t.palette.text.secondary}>{t.there_is_nothing[lang]}</Typography>
        )}

        {!isOrdersLoading && hasOrders && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorPrev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.grey[400]} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={palette.grey[400]} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.primary.main} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={palette.background.paper} stopOpacity={0.6} />
                </linearGradient>
              </defs>

              <XAxis dataKey="title" />

              <Tooltip<string, string>
                formatter={(value) => getCurrency(value)}
                labelFormatter={(_, p) => p?.[0]?.payload?.title}
                contentStyle={{ borderRadius: '8px' }}
              />
              <Area
                type="monotone"
                dataKey={t.last_week[lang]}
                stroke={palette.grey[400]}
                dot={false}
                activeDot={{ r: 2 }}
                fillOpacity={1}
                fill="url(#colorPrev)"
              />
              <Area
                type="monotone"
                dataKey={t.current_period[lang]}
                stroke={palette.primary.main}
                strokeWidth={2}
                dot={false}
                fillOpacity={1}
                fill="url(#colorCurrent)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>

      <Box textAlign="right">
        <PrimaryButton variant="text" size="small" color="secondary" onClick={navigateToArchive}>
          {t.order_archive[lang]}
        </PrimaryButton>
      </Box>
    </StyledPaper>
  );
};

export default IncomeChart;
